import { useMemo, useRef, useState } from "react";
import { useAtom } from "jotai";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import { CloseButton } from "../../../components/buttons/CloseButton";
import { IconTypes } from "../../../components/icons.types";
import { Product } from "../../../features/products/product";
import { productDbAtom } from "../../../features/products/product.state";
import { DeleteByCodeOptions } from "../../../features/products/product-modal-state";

interface DeleteByCodeModalProps extends DeleteByCodeOptions {
  onClose: () => void;
}

// Excel column / comma / semicolon / tab separated paste.
const splitCodes = (text: string): string[] => {
  const seen = new Set<string>();
  const codes: string[] = [];
  for (const raw of text.split(/[\r\n,;\t]+/)) {
    const code = raw.trim();
    if (!code) continue;
    const key = code.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    codes.push(code);
  }
  return codes;
};

type MatchResult = {
  products: Product[];
  matchedCodeCount: number;
  notFound: string[];
};

export const DeleteByCodeModal = ({
  onConfirm,
  onClose,
}: DeleteByCodeModalProps) => {
  const [productDb] = useAtom(productDbAtom);
  const [text, setText] = useState("");
  const [result, setResult] = useState<MatchResult | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // code (lower-cased) -> products. Codes are not guaranteed unique per
  // account, so a single code may resolve to more than one product.
  const codeIndex = useMemo(() => {
    const index = new Map<string, Product[]>();
    for (const ref of productDb.productMap.values()) {
      const code = ref.product.code?.trim().toLowerCase();
      if (!code) continue;
      const list = index.get(code);
      if (list) list.push(ref.product);
      else index.set(code, [ref.product]);
    }
    return index;
  }, [productDb.productMap]);

  const match = () => {
    const codes = splitCodes(text);
    const seenIds = new Set<string>();
    const products: Product[] = [];
    const notFound: string[] = [];
    let matchedCodeCount = 0;
    for (const code of codes) {
      const hits = codeIndex.get(code.toLowerCase());
      if (hits?.length) {
        matchedCodeCount++;
        for (const p of hits) {
          if (!seenIds.has(p.id)) {
            seenIds.add(p.id);
            products.push(p);
          }
        }
      } else {
        notFound.push(code);
      }
    }
    setResult({ products, matchedCodeCount, notFound });
  };

  const readFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = () => {
      setText((reader.result as string) ?? "");
      setResult(null);
    };
    reader.readAsText(file);
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) readFile(file);
  };

  const ids = result?.products.map((p) => p.id) ?? [];
  const hasDuplicates =
    !!result && result.products.length > result.matchedCodeCount;

  return (
    <Dialog open={true} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Delete products by code
        <CloseButton onClose={onClose} />
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 1.5 }}>
          Paste your product codes below — one per line, or separated by commas.
          You can also drop or upload a .csv / .txt file.
        </DialogContentText>

        <Box
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          sx={{
            borderRadius: 1,
            outline: dragOver ? "2px dashed" : "none",
            outlineColor: "primary.main",
          }}
        >
          <TextField
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              setResult(null);
            }}
            placeholder={"190200329\n190200330\nTK-1"}
            multiline
            minRows={6}
            maxRows={14}
            fullWidth
          />
        </Box>

        <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 1 }}>
          <Button size="small" onClick={() => fileInputRef.current?.click()}>
            Upload file
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.txt,text/csv,text/plain"
            hidden
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) readFile(file);
              e.target.value = "";
            }}
          />
          <Box sx={{ flexGrow: 1 }} />
          <Button
            variant="outlined"
            size="small"
            onClick={match}
            disabled={!text.trim()}
          >
            Match products
          </Button>
        </Box>

        {result && (
          <Box
            sx={{
              mt: 2,
              p: 1.5,
              borderRadius: 1,
              bgcolor: "action.hover",
            }}
          >
            <Typography variant="body2">
              <b>{result.products.length}</b> product(s) matched
              {hasDuplicates && (
                <> from {result.matchedCodeCount} code(s)</>
              )}
              .
            </Typography>
            {hasDuplicates && (
              <Typography variant="caption" color="text.secondary">
                Some codes match more than one product — all matches will be
                deleted.
              </Typography>
            )}
            {result.notFound.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" color="warning.main">
                  {result.notFound.length} code(s) not found:
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  component="div"
                  sx={{
                    mt: 0.5,
                    maxHeight: 90,
                    overflowY: "auto",
                    wordBreak: "break-all",
                  }}
                >
                  {result.notFound.join(", ")}
                </Typography>
              </Box>
            )}
            {result.products.length === 0 && (
              <Typography variant="caption" color="text.secondary">
                Check the codes and try again.
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          color="error"
          startIcon={<IconTypes.DeleteAction />}
          disabled={ids.length === 0}
          onClick={() => {
            onConfirm(ids);
            onClose();
          }}
        >
          Delete {ids.length > 0 ? ids.length + " " : ""}product(s)
        </Button>
      </DialogActions>
    </Dialog>
  );
};
